/**
 * MonthlySummaryPage.js — Resumen Financiero mensual del CEO.
 *
 * KPIs del mes cerrado (mes calendario anterior). Breakdown:
 *   - Total volumen UYU
 *   - Ganancia bruta
 *   - CEO neto
 * + Detalle por gestor (25%) y por proveedor (50%).
 * + Botón "Enviarme el email ahora" (re-dispara el cron manualmente).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { Mail, RefreshCw, TrendingUp } from 'lucide-react';
import { extractErrorMessage } from '@/utils/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) =>
  Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MonthlySummaryPage = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/monthly-summary/preview`, { headers: getAuthHeaders() });
      setData(r.data);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo cargar el resumen'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const sendEmail = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/email/send-monthly-ceo-summary`, {}, { headers: getAuthHeaders() });
      toast.success('Email enviado en segundo plano. Llega en ~30s.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo enviar el email'));
    } finally {
      setSending(false);
    }
  };

  const kpis = data?.kpis || {};
  const period = data?.period || '—';

  return (
    <div className="space-y-4" data-testid="monthly-summary-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-emerald-600" /> Resumen Mensual CEO
          </h1>
          <p className="text-sm text-slate-500 mt-1">Mes cerrado: <strong>{period}</strong> · split v3 (50% Proveedor · 25% CEO · 25% Gestor)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={load} disabled={loading} data-testid="refresh-summary">
            <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
          </Button>
          <Button onClick={sendEmail} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-summary-email">
            <Mail className="w-4 h-4 mr-1" /> {sending ? 'Enviando…' : 'Enviarme el email ahora'}
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { label: 'Volumen total UYU', value: kpis.total_volume_uyu, tone: 'bg-teal-50 border-teal-200 text-teal-700' },
          { label: 'Ganancia bruta',    value: kpis.gross_profit_uyu, tone: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
          { label: 'CEO neto',          value: kpis.ceo_net_uyu,      tone: 'bg-blue-50 border-blue-200 text-blue-700' },
        ].map((k) => (
          <Card key={k.label} className={`p-4 border ${k.tone}`} data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]/g,'-')}`}>
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{k.label}</p>
            <p className="text-2xl font-bold font-mono mt-1">${fmt(k.value)}</p>
          </Card>
        ))}
      </div>

      <p className="text-xs text-slate-500">
        Comisiones externas: <strong>${fmt(kpis.external_fees_uyu)}</strong> · # remesas entregadas: <strong>{kpis.ops_count || 0}</strong>
      </p>

      {/* Per-gestor */}
      <Card className="border-0 shadow-sm">
        <div className="p-3 border-b font-semibold text-slate-700 text-sm">Detalle por Gestor (25%)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <th className="p-2 text-left">Gestor</th>
                <th className="p-2 text-right"># remesas</th>
                <th className="p-2 text-right">Total UYU</th>
                <th className="p-2 text-right">Ganancia 25%</th>
              </tr>
            </thead>
            <tbody>
              {(data?.gestores || []).length === 0 ? (
                <tr><td colSpan={4} className="p-4 text-center text-slate-400">Sin gestores con ventas este período.</td></tr>
              ) : data.gestores.map((g, i) => (
                <tr key={g.id || g.email || `gestor-${i}`} className="border-t">
                  <td className="p-2">{g.name}</td>
                  <td className="p-2 text-right font-mono">{g.count}</td>
                  <td className="p-2 text-right font-mono">${fmt(g.total_uyu)}</td>
                  <td className="p-2 text-right font-mono text-emerald-600 font-semibold">${fmt(g.profit_gestor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Per-provider */}
      <Card className="border-0 shadow-sm">
        <div className="p-3 border-b font-semibold text-slate-700 text-sm">Detalle por Proveedor (50%)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <th className="p-2 text-left">Proveedor</th>
                <th className="p-2 text-right"># remesas</th>
                <th className="p-2 text-right">Total UYU</th>
                <th className="p-2 text-right">Ganancia 50%</th>
              </tr>
            </thead>
            <tbody>
              {(data?.providers || []).length === 0 ? (
                <tr><td colSpan={4} className="p-4 text-center text-slate-400">Sin proveedores con ventas este período.</td></tr>
              ) : data.providers.map((p, i) => (
                <tr key={p.id || p.email || `provider-${i}`} className="border-t">
                  <td className="p-2">{p.name}</td>
                  <td className="p-2 text-right font-mono">{p.count}</td>
                  <td className="p-2 text-right font-mono">${fmt(p.total_uyu)}</td>
                  <td className="p-2 text-right font-mono text-emerald-600 font-semibold">${fmt(p.profit_provider)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-[11px] text-slate-400 text-center">
        El resumen se envía automáticamente el primer día de cada mes a las 08:00 UTC.
      </p>
    </div>
  );
};

export default MonthlySummaryPage;
